import { Box } from '@mui/material';
import {
  ChartTypeRegistry as _ChartTypeRegistry,
  LineController,
} from 'chart.js';
import type { ChartData, ChartOptions, ChartType } from 'chart.js';
import { Chart as ChartJS, registerables } from 'chart.js';
import annotationPlugin from 'chartjs-plugin-annotation';
import { usePubSub } from 'common/Context';
import { Platform } from 'componentregistry';
import { CallResult } from 'controller/ControllerInterface';
import FileSaver from 'file-saver';
import useDebounce from 'hooks/useDebounce';
import Papa from 'papaparse';
import React, { forwardRef, useEffect, useRef, useState } from 'react';
import { useTheme } from 'theme/Theme';
import { useTranslate } from '../../utils/lang';
import { DBRow } from '@cloudio-saas/datasource-types';
import ChartDataLabels from 'chartjs-plugin-datalabels';
import { backgroundColorFn } from 'theme/useThemeColor';

declare module 'chart.js' {
  interface ChartTypeRegistry {
    LineWithLine: _ChartTypeRegistry['line'];
  }
}

let divider = '#ddd';
class Custom extends LineController {
  draw() {
    // console.log(this.chart.tooltip);
    // @ts-ignore _active
    var activePoint = this.chart.tooltip?._active?.[0];
    if (activePoint) {
      var ctx = this.chart.ctx;
      var x = activePoint.element.x;
      var topY = this.chart.scales.y.top;
      var bottomY = this.chart.scales.y.bottom;

      // draw line
      ctx.save();
      ctx.beginPath();
      ctx.moveTo(x, topY);
      ctx.lineTo(x, bottomY);
      ctx.lineWidth = 1;
      ctx.strokeStyle = divider;
      ctx.stroke();
      ctx.restore();
    }
    // @ts-ignore arguments
    super.draw(arguments);
  }
}

Custom.id = 'LineWithLine';
Custom.defaults = LineController.defaults;
ChartJS.register(...(registerables ?? []), Custom);
ChartJS.register(annotationPlugin);

export interface ClickHandlerProps {
  category?: string | number;
  label: string | number;
  value: number;
  record: DBRow<any>;
}

interface Props {
  appUid: string;
  backgroundColor: string;
  children: React.ReactNode;
  className?: string;
  data: ChartData;
  enableDatalabels: boolean;
  height?: number;
  hideTooltips: boolean;
  id?: string;
  itemId: string;
  onChartClick?: (props: ClickHandlerProps) => void;
  options?: ChartOptions;
  pageId: string;
  platform: Platform;
  preRenderCallback: (
    props: Record<string, unknown>,
  ) => Promise<CallResult<unknown>>;
  redraw?: boolean;
  type: ChartType;
  width?: number;
  dsalias: string;
}

const ChartComponent = forwardRef(
  (props: Props, ref: React.Ref<HTMLDivElement>) => {
    const {
      appUid,
      backgroundColor,
      children,
      className,
      data,
      enableDatalabels,
      height = 150,
      hideTooltips,
      id,
      itemId,
      onChartClick,
      options = {},
      pageId,
      platform,
      preRenderCallback,
      type,
      width = 300,
      dsalias,
    } = props;
    const theme = useTheme();
    const { t } = useTranslate();
    const chartBgColor = backgroundColorFn(theme, backgroundColor);
    divider = theme.palette.divider;

    const canvasRef = useRef<HTMLCanvasElement>(null);
    const [chart, setChart] = useState<ChartJS | undefined>(undefined);
    const chartInstanceRef = useRef<ChartJS | null>(null);

    // useImperativeHandle<ChartJS | undefined, ChartJS | undefined>(
    //   ref,
    //   () => chart,
    //   [chart],
    // );

    const handleClick = (e: React.MouseEvent<HTMLCanvasElement>) => {
      if (!chart) return;
      // const clickIndex = chart.getElementsAtEventForMode(
      //   e.nativeEvent,
      //   'index',
      //   { intersect: true },
      //   false,
      // ) as { datasetIndex: number; index: number }[];
      const clickIndex = chart.getElementsAtEventForMode(
        e.nativeEvent,
        'nearest',
        { intersect: true },
        true,
      );
      if (
        data?.labels &&
        Array.isArray(clickIndex) &&
        clickIndex.length === 1
      ) {
        const { datasetIndex, index } = clickIndex[0];
        const dataset = data.datasets[datasetIndex];
        const label = data.labels[index] as string;
        const value = dataset.data[index] as number;
        // @ts-ignore records
        const record = dataset.records[index];
        onChartClick?.({ category: dataset.label, label, value, record });
      }
    };

    const [setDebounceValue] = useDebounce(async () => {
      if (!canvasRef.current) return;
      if (chart) {
        chart.destroy();
      }
      try {
        const ctx = canvasRef.current.getContext('2d');
        if (ctx) {
          ctx.canvas.height = height;
          ctx.canvas.width = width;
          await preRenderCallback({
            data,
            height,
            options,
            platform,
            theme,
            type,
            width,
            pageId,
            itemId,
            appUid,
            t,
          });
          const _chart = new ChartJS(ctx, {
            type: type === 'line' && !hideTooltips ? 'LineWithLine' : type,
            data,
            plugins: [
              ...(enableDatalabels ? [ChartDataLabels] : []),
              {
                id: 'bgcolor',
                beforeDraw: (__chart) => {
                  const c = __chart.canvas.getContext('2d');
                  if (c) {
                    c.save();
                    c.globalCompositeOperation = 'destination-over';
                    c.fillStyle = chartBgColor;
                    c.fillRect(0, 0, __chart.width, __chart.height);
                    c.restore();
                  }
                },
              },
            ],
            options,
          });
          chartInstanceRef.current = _chart;
          setChart(_chart);
        }
      } catch (e) {
        console.error(e);
      }
    });

    useEffect(() => {
      setDebounceValue(100);
    }, [data, options, type, width, height, setDebounceValue]);

    const pubSub = usePubSub();

    useEffect(() => {
      if (!(pubSub && chart)) {
        return;
      }
      return pubSub.sub(
        `dwn-cjs-png-${appUid}-${pageId}-${itemId}`,
        async (e, { fileName, imageType = 'png' }) => {
          let b64;
          const chartInstance = chartInstanceRef.current;
          if (!chartInstance) {
            return;
          }
          if (imageType === 'jpeg') {
            b64 = chart.toBase64Image('image/jpeg', 1);
          } else {
            b64 = chart.toBase64Image();
          }
          if (b64) {
            const link = document.createElement('a');
            link.download = fileName
              ? `${fileName}.${imageType}`
              : dsalias
              ? `${dsalias}.${imageType}`
              : `chart-image.${imageType}`;
            link.href = b64;
            link.click();
          }
        },
      );
    }, [pubSub, chart, appUid, pageId, itemId, dsalias]);

    useEffect(() => {
      if (!(pubSub && chart)) {
        return;
      }
      return pubSub.sub(
        `dwn-cjs-csv-${appUid}-${pageId}-${itemId}`,
        async (e, { fileName }) => {
          const { labels, datasets } = data;
          if (!(labels?.length && datasets.length)) {
            return;
          }
          const lines: (string | number)[][] = [];
          if (datasets.length > 1) {
            const headers: (string | number)[] = ['Label'];
            datasets.forEach((ds) => {
              headers.push(ds.label ?? '');
            });
            lines.push(headers);
            (labels as string[]).forEach((label, index) => {
              const values: (string | number)[] = [label];
              datasets.forEach((ds) => {
                values.push((ds.data[index] as number) ?? 0);
              });
              lines.push(values);
            });
          } else {
            const d = datasets[0].data as number[];
            lines.push([
              // @ts-ignore line chart
              options.scales?.x?.title?.text || 'Label',
              // @ts-ignore line chart
              options.scales?.y?.title?.text || 'Value',
            ]);
            (labels as string[]).forEach((label, index) => {
              lines.push([label, d[index]]);
            });
          }
          const csvData = Papa.unparse(lines);
          var blob = new Blob([csvData], { type: 'text/csv;charset=utf-8;' });
          FileSaver.saveAs(blob, (fileName as string) || 'chart-data.csv');
        },
      );
    }, [pubSub, chart, appUid, pageId, itemId, data, options]);

    return (
      <Box
        ref={ref}
        sx={{ width, height, position: 'relative', cursor: 'default' }}
      >
        <canvas
          ref={canvasRef}
          id={id}
          className={className}
          onClick={onChartClick ? handleClick : undefined}
          role="button"
        />
        {children}
      </Box>
    );
  },
);

export default ChartComponent;
